import { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { PlusIcon, PencilIcon, TrashIcon, ExclamationTriangleIcon, BuildingLibraryIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { formatCurrency } from '../../utils/formatters';
import { getMortgagePaymentInfo, calculateTotalInterest } from '../../utils/mortgageCalculations';
import MortgageModal from './MortgageModal';
import dayjs from 'dayjs';

const Mortgages = () => {
  const navigate = useNavigate();
  const { mortgages, properties, deleteMortgage } = useData();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingMortgage, setEditingMortgage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mortgageToDelete, setMortgageToDelete] = useState(null);

  const handleEdit = (e, mortgage) => {
    e.stopPropagation();
    setEditingMortgage(mortgage);
    setIsModalVisible(true);
  };

  const handleDelete = () => {
    if (mortgageToDelete) {
      deleteMortgage(mortgageToDelete.id);
      setDeleteDialogOpen(false);
      setMortgageToDelete(null);
    }
  };

  const handleModalClose = () => {
    setEditingMortgage(null);
    setIsModalVisible(false);
  };

  const getLinkedProperty = (mortgage) => {
    return properties.find(p => p.id === mortgage.linkedPropertyId);
  };

  const totalBalance = mortgages.reduce((sum, m) => sum + (m.currentBalance || 0), 0);
  const totalMonthlyPayments = mortgages.reduce((sum, m) => {
    const info = getMortgagePaymentInfo(m);
    return sum + info.monthlyPayment + (m.overpaymentMonthly || 0);
  }, 0);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold m-0">Mortgages</h3>
        <button
          onClick={() => setIsModalVisible(true)}
          className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium transition-colors shadow-sm"
          style={{ backgroundColor: '#012E58' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
        >
          <PlusIcon className="w-4 h-4" />
          <span>Add Mortgage</span>
        </button>
      </div>

      {/* Summary Cards */}
      {mortgages.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Outstanding</p>
            <p className="text-xl font-bold text-red-600 mt-1">{formatCurrency(totalBalance, 'GBP')}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Monthly Payments</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(totalMonthlyPayments, 'GBP')}</p>
          </div>
        </div>
      )}

      {mortgages.length === 0 ? (
        <div className="mt-6">
          <div className="flex flex-col items-center gap-4">
            <BuildingLibraryIcon className="w-12 h-12 text-gray-300" />
            <p className="text-gray-600">No mortgages added yet</p>
            <button
              onClick={() => setIsModalVisible(true)}
              className="px-4 py-2 text-white rounded-lg font-medium transition-colors shadow-sm"
              style={{ backgroundColor: '#012E58' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
            >
              Add Your First Mortgage
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {mortgages.map(mortgage => {
            const paymentInfo = getMortgagePaymentInfo(mortgage);
            const linkedProperty = getLinkedProperty(mortgage);
            const fixedEnd = dayjs(mortgage.fixedTermEndDate);
            const totalInterestInfo = calculateTotalInterest(mortgage);

            return (
              <div
                key={mortgage.id}
                onClick={() => navigate(`/mortgages/${mortgage.id}`)}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-gray-900 truncate">{mortgage.name}</h4>
                    {mortgage.lender && (
                      <p className="text-xs text-gray-500 mt-0.5">{mortgage.lender}</p>
                    )}
                  </div>
                  <div className="flex gap-1 ml-2">
                    <button onClick={(e) => handleEdit(e, mortgage)} className="p-1 text-gray-400 hover:text-gray-600" title="Edit">
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setMortgageToDelete(mortgage); setDeleteDialogOpen(true); }} className="p-1 text-gray-400 hover:text-red-600" title="Delete">
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Balance</span>
                    <span className="text-sm font-bold text-red-600">{formatCurrency(mortgage.currentBalance, mortgage.currency)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Monthly Payment</span>
                    <span className="text-sm font-medium text-gray-900">{formatCurrency(paymentInfo.monthlyPayment, mortgage.currency)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Rate</span>
                    <span className="text-sm font-medium text-gray-900">
                      {paymentInfo.currentRate}%
                      <span className={`text-xs ml-1 px-1.5 py-0.5 rounded-full ${paymentInfo.isInFixedPeriod ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {paymentInfo.isInFixedPeriod ? 'Fixed' : 'SVR'}
                      </span>
                    </span>
                  </div>
                  {paymentInfo.isInFixedPeriod && (
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">Fixed until</span>
                      <span className="text-xs text-gray-700">{fixedEnd.format('MMM YYYY')}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Remaining</span>
                    <span className="text-xs text-gray-700">
                      {Math.floor(paymentInfo.remainingMonths / 12)}y {paymentInfo.remainingMonths % 12}m
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Total Interest</span>
                    <span className="text-xs text-gray-700">{formatCurrency(totalInterestInfo.totalInterest, mortgage.currency)}</span>
                  </div>
                </div>

                {linkedProperty && (
                  <div className="mt-3 pt-2 border-t border-gray-100">
                    <span className="text-xs text-gray-400">Linked to: {linkedProperty.name}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <MortgageModal visible={isModalVisible} onCancel={handleModalClose} mortgage={editingMortgage} />

      {/* Delete confirmation dialog */}
      <Transition appear show={deleteDialogOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setDeleteDialogOpen(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-sm transform rounded-2xl bg-white p-6 text-left shadow-xl transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                    <Dialog.Title as="h3" className="text-base font-semibold text-gray-900">Delete Mortgage</Dialog.Title>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Are you sure you want to delete "{mortgageToDelete?.name}"? This will unlink any associated property but not delete it.
                  </p>
                  <div className="flex gap-3 justify-end">
                    <button onClick={() => setDeleteDialogOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                    <button onClick={handleDelete} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">Delete</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default Mortgages;
